'use client';

import React, { useRef, useLayoutEffect } from 'react';
import { usePathname } from 'next/navigation';
import CTAButton from '../ui/CTAButton';
import { trackEvent } from '@/lib/analytics';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import LayoutAboutTitleCarouselMobile from './LayoutAboutTitleCarouselMobile';
import LayoutAboutTitleCarouselDesktop from './LayoutAboutTitleCarouselDesktop';

gsap.registerPlugin(ScrollTrigger);

/**
 * LayoutAboutTitleCarousel - Main component that displays a title with animated text
 * and carousels for the "Join our team" section.
 * 
 * Contains the heading section, mobile carousel, desktop carousel, and CTA button.
 * 
 * @component
 */
export default function LayoutAboutTitleCarousel() {
  const pathname = usePathname();
  const experienceRef = useRef<HTMLSpanElement>(null);
  const experienceUnderlineRef = useRef<SVGPathElement>(null);
  const brightRef = useRef<HTMLSpanElement>(null);

  // Carousel images with explicit widths (at 500px height) for desktop
  const carouselData = [
    { src: '/images/about-carousel/about-carousel-desktop-01.jpg', width: 600, alt: 'Team members at work' },
    { src: '/images/about-carousel/about-carousel-desktop-02.jpg', width: 420, alt: 'Team member at work' },
    { src: '/images/about-carousel/about-carousel-desktop-03.jpg', width: 420, alt: 'Team members at work' },
    { src: '/images/about-carousel/about-carousel-desktop-04.jpg', width: 600, alt: 'Team meeting' },
    { src: '/images/about-carousel/about-carousel-desktop-05.jpg', width: 400, alt: 'Team members at work' },
    { src: '/images/about-carousel/about-carousel-desktop-06.jpg', width: 400, alt: 'Team member at work' },
    { src: '/images/about-carousel/about-carousel-desktop-07.jpg', width: 600, alt: 'Team member at work' },
    { src: '/images/about-carousel/about-carousel-desktop-08.jpg', width: 400, alt: 'Team member at work' },
  ];

  // Mobile carousel images - separate from desktop (update these paths manually)
  const carouselDataMobile = [
    { src: '/images/about-carousel/about-carousel-mobile-01.jpg', width: 400, alt: 'Team members at work' },
    { src: '/images/about-carousel/about-carousel-mobile-02.jpg', width: 400, alt: 'Team member at work' },
    { src: '/images/about-carousel/about-carousel-mobile-03.jpg', width: 400, alt: 'Team members at work' },
    { src: '/images/about-carousel/about-carousel-mobile-04.jpg', width: 400, alt: 'Team meeting' },
    { src: '/images/about-carousel/about-carousel-mobile-05.jpg', width: 400, alt: 'Team members at work' },
    { src: '/images/about-carousel/about-carousel-mobile-06.jpg', width: 400, alt: 'Team member at work' },
    { src: '/images/about-carousel/about-carousel-mobile-07.jpg', width: 400, alt: 'Team member at work' },
  ];

  // Animate underline for "Experience" when it scrolls into view
  useLayoutEffect(() => {
    if (!experienceUnderlineRef.current) return;

    const path = experienceUnderlineRef.current;
    const pathLength = path.getTotalLength();

    // Set up the initial state
    gsap.set(path, {
      strokeDasharray: pathLength,
      strokeDashoffset: pathLength,
    });

    const context = gsap.context(() => {
      gsap.to(path, {
        strokeDashoffset: 0,
        duration: 1.2,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: experienceRef.current,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      });
    });

    return () => context.revert();
  }, []);

  // Animate hand-drawn mark over the word "bright"
  useLayoutEffect(() => {
    if (!brightRef.current) return;

    const paths = brightRef.current.querySelectorAll('path');
    if (!paths.length) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: brightRef.current,
        start: 'top 85%',
        toggleActions: 'play none none none',
      },
    });

    paths.forEach((p, i) => {
      const len = (p as SVGPathElement).getTotalLength();
      gsap.set(p, {
        strokeDasharray: len,
        strokeDashoffset: len,
        fill: 'none',
        stroke: '#E60000',
        strokeWidth: 2.5,
        strokeLinecap: 'round',
      });
      tl.to(p, { strokeDashoffset: 0, duration: 0.5 + i * 0.1, ease: 'power1.out' }, i * 0.1);
    });

    return () => {
      try { tl.kill(); } catch {}
    };
  }, []);

  return (
    <>
      <section className="global-padding">
        {/* Heading section */}
        <div className="heading mx-auto">
          <h2 className="heading-2 mb-3">Join our team</h2>
          <h3 className="body-xl-display mb-[55px] lg:mb-[90px]">
            If you&rsquo;ve got <span ref={brightRef} className="relative inline-block">bright
              <svg
                className="absolute -top-[20px] right-0 md:-top-[40px] w-[40px] h-[20px] md:w-[81px] md:h-[41px] pointer-events-none"
                width="81"
                height="41"
                viewBox="0 0 81 41"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path d="M29.7452 7.98878C29.9512 7.94421 30.1671 7.98316 30.3485 8.09642C30.5296 8.20971 30.6615 8.38788 30.7119 8.5926C30.8104 8.99454 30.9083 9.39467 31.0067 9.79624C32.5076 15.9141 33.9828 22.0469 35.4928 28.1482C35.767 29.2448 36.04 30.3396 36.3302 31.3996C36.4328 31.767 36.5563 32.2105 36.6335 32.338C36.8014 32.6981 36.817 33.1064 36.6707 33.4758C36.5248 33.8451 36.229 34.1452 35.8544 34.3074C35.4797 34.4697 35.0586 34.4804 34.6894 34.3342C34.3198 34.1882 34.0327 33.8972 33.8849 33.5283C33.6365 32.8999 33.5896 32.5251 33.4877 32.0987C33.2426 30.9606 33.032 29.8585 32.816 28.7376C31.6391 22.5382 30.5058 16.3464 29.3433 10.1554C29.2672 9.74925 29.1912 9.34476 29.1149 8.93825C29.0761 8.73103 29.1225 8.51416 29.2405 8.33612C29.3588 8.15806 29.5392 8.03338 29.7452 7.98878Z" fill="#E60000"/>
                <path d="M1.21519 21.5211C1.66013 21.3713 2.14378 21.4099 2.56122 21.6246C2.92111 21.8097 3.27862 21.9939 3.63776 22.179C8.99403 24.9428 14.3322 27.7284 19.6301 30.6197C20.7233 31.2204 21.7627 31.8704 22.8371 32.579C23.1799 32.8078 23.5763 33.0745 23.9243 33.36C24.1634 33.554 24.3179 33.8437 24.3597 34.157C24.4011 34.4709 24.3265 34.783 24.1463 35.0324C23.966 35.2817 23.6932 35.4504 23.3823 35.5095C23.0717 35.5682 22.7483 35.5124 22.489 35.3463C22.1885 35.152 21.8941 35.0082 21.5391 34.8343C20.4642 34.3168 19.3325 33.8266 18.2349 33.2703C12.8818 30.5765 7.4841 27.9213 2.07479 25.3174C1.71191 25.1429 1.35042 24.9695 0.986761 24.7953C0.563381 24.5926 0.240616 24.2306 0.0909649 23.7857C-0.0587948 23.3408 -0.023088 22.8492 0.188652 22.4227C0.400404 21.9963 0.770361 21.6707 1.21519 21.5211Z" fill="#E60000"/>
                <path d="M73.9921 4.28369C74.3151 4.05248 74.7154 3.96058 75.1064 4.03104C75.4972 4.10136 75.847 4.32819 76.0768 4.6589C76.3066 4.98966 76.3972 5.39672 76.3268 5.78757C76.2565 6.17857 76.0307 6.5216 75.7013 6.74369C75.1557 7.11177 74.6117 7.47862 74.0703 7.84663C70.6954 10.1242 67.462 12.526 64.3282 15.1191C58.5375 20.0395 53.1443 25.7057 47.4346 30.9801C46.9521 31.4274 46.4596 31.8797 45.9717 32.321C45.7868 32.4883 45.541 32.5754 45.2903 32.5646C45.0395 32.5537 44.804 32.446 44.6343 32.2635C44.4645 32.081 44.3739 31.8382 44.3812 31.5872C44.3886 31.3363 44.4932 31.0976 44.6733 30.9253C45.1422 30.4769 45.6169 30.0168 46.0821 29.5609C51.6139 24.1641 56.7716 18.4166 62.6456 13.1347C65.7546 10.4132 69.0308 7.83664 72.3834 5.43781C72.9202 5.05085 73.4568 4.6667 73.9921 4.28369Z" fill="#E60000"/>
              </svg>
            </span> ideas and big talent, join us in helping shape the future of <span ref={experienceRef} className="relative inline-block">experience
              <svg
                className="absolute -bottom-1 left-0 w-full h-auto max-w-[200px] md:max-w-[282px]"
                width="282"
                height="10"
                viewBox="0 0 282 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  ref={experienceUnderlineRef}
                  d="M1.24997 8.14796C3.29882 7.72724 5.34767 7.30652 48.8881 5.95246C92.4286 4.59841 177.399 2.32378 222.795 1.36836C271.438 0.845656 277.896 2.19179 280.678 2.74333"
                  stroke="#E60000"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </svg>
            </span>.
          </h3>
        </div>
      </section>

      {/* Mobile carousel */}
      <LayoutAboutTitleCarouselMobile carouselData={carouselDataMobile} />

      {/* Desktop carousel */}
      <LayoutAboutTitleCarouselDesktop carouselData={carouselData} />

      {/* CTA Button */}
      <section className="global-padding">
        <div className="mb-[60px] mx-auto text-center">
          <CTAButton 
            href="/careers"
            onClick={() => {
              trackEvent('who_we_are_click', {
                click_text: 'Explore careers',
                click_url: '/careers',
                page_path: pathname
              });
            }}
          >
            Explore careers
          </CTAButton>
        </div>
      </section>
    </>
  );
}

